<template>
  <div class="teleprompter shadow-sm fixed-top">
    <div class="teleprompter-header d-flex align-items-center px-4">
      <multiselect
        v-model="scriptSelected"
        placeholder="Search/select teleprompter script"
        label="name"
        track-by="hash_id"
        :options="scripts"
        :loading="isLoading"
        :internal-search="false"
        :show-no-options="false"
        @select="onSelect"
        @search-change="debounceSearchScripts"
      >
        <template #beforeList>
          <li class="multiselect__element">
            <router-link :to="{ name: 'dashboard.scripts.create' }" target="_blank">
              <span class="multiselect__option multiselect__option--link">
                <span>Create new script</span>
              </span>
            </router-link>
          </li>
        </template>
      </multiselect>
    </div>

    <div v-if="scriptSelected && !isLoading">
      <div ref="teleprompter" class="teleprompter-body p-4" :style="{fontSize: fontSize + 'px'}">
        <p v-for="line in scriptSelected.script.split('\n')">
          {{ line }}
        </p>
      </div>
      <div class="teleprompter-toolbar px-4 py-2 d-flex justify-content-between align-items-center">
        <a
          class="btn btn-icon btn-clean btn-sm hint--bottom hint--rounded"
          aria-label="Close"
          @click="close"
        >
          <i class="fal fa-chevron-up"></i>
        </a>
        <input
          v-model="wordsPerMinute"
          type="range"
          min="100"
          max="300"
          class="ml-2 slider hint--bottom hint--rounded"
          :aria-label="wordsPerMinute + ' WPM'"
        />

        <a
          v-if="isScrolling"
          class="btn btn-icon btn-clean btn-sm ml-2 hint--bottom hint--rounded"
          aria-label="Pause"
          @click="pause"
        >
          <i class="fal fa-pause"></i>
        </a>

        <button
          v-else
          class="btn btn-icon btn-clean btn-sm ml-2 hint--bottom hint--rounded"
          aria-label="Scroll"
          :disabled="isAllTextVisible"
          @click="scroll"
        >
          <i class="fal fa-play"></i>
        </button>

        <button
          class="btn btn-icon btn-clean btn-sm ml-2 hint--bottom hint--rounded"
          aria-label="Back to the top"
          :disabled="isAllTextVisible"
          @click="scrollFromTop"
        >
          <i class="fal fa-redo"></i>
        </button>

        <a
          class="btn btn-icon btn-clean btn-sm mr-2 hint--bottom hint--rounded"
          aria-label="Zoom out text"
          @click="decreaseFontSize"
        >
          <i class="fal fa-minus"></i>
        </a>
        <a
          class="btn btn-icon btn-clean btn-sm hint--bottom hint--rounded"
          aria-label="Zoom in text"
          @click="increaseFontSize"
        >
          <i class="fal fa-plus"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Store from 'store'
import $ from 'jquery'
import debounce from 'lodash/debounce'

export default {
  name: 'Teleprompter',
  data () {
    return {
      scriptSelected: null,
      scripts: [],
      isLoading: false,
      isScrolling: false,
      fontSize: 16,
      wordsPerMinute: 150,
      isAllTextVisible: false
    }
  },

  watch: {
    wordsPerMinute (newVal) {
      Store.set('telepromper::words-per-minute', newVal)
      if (this.isScrolling) {
        this.scroll()
      }
    },

    scriptSelected () {
      this.$nextTick(() => {
        const el = this.$refs.teleprompter
        if (el) {
          this.isAllTextVisible = el.scrollHeight <= el.clientHeight
        }
      })
    }
  },

  mounted () {
    Store.getWithCallback('telepromper::font-size', ret => {
      this.fontSize = ret
    })

    Store.getWithCallback('telepromper::words-per-minute', ret => {
      this.wordsPerMinute = ret
    })

    this.searchScripts('')
  },
  created () {
    this.debounceSearchScripts = debounce(this.searchScripts.bind(this), 500)
  },
  methods: {
    onSelect (selectedOption) {
      this.loadScript(selectedOption.hash_id)
    },

    searchScripts (query) {
      if (!Spark.state.user.current_team_id) {
        return
      }

      this.isLoading = true
      axios.get(this.router('api.script.index', { filter: { search: query } }))
        .then(resp => {
          this.isLoading = false
          this.scripts = resp.data.data
        })
    },

    loadScript (hashId) {
      this.isLoading = true
      axios.get(this.router('api.script.show', { script: hashId }))
        .then(resp => {
          this.scriptSelected = resp.data
          this.isLoading = false
        })
    },

    setFontSize (size) {
      if (size <= 10) {
        size = 10
      }

      this.fontSize = size
      Store.set('telepromper::font-size', this.fontSize)
    },

    increaseFontSize () {
      this.setFontSize(this.fontSize + 1)
    },

    decreaseFontSize () {
      this.setFontSize(this.fontSize - 1)
    },

    close () {
      this.scriptSelected = null
    },

    pause () {
      $(this.$refs.teleprompter).first().stop(true)
      this.isScrolling = false
    },

    scrollFromTop () {
      $(this.$refs.teleprompter).first().scrollTop(0)
      this.scroll()
    },

    scroll () {
      const $body = $(this.$refs.teleprompter).first()

      $body.stop(true)

      $(this.$refs.teleprompter).find('p').each((_, p) => {
        const bottom = p.offsetTop + 16 + p.scrollHeight - $body.innerHeight()

        const distance = bottom - $body.scrollTop()
        if (distance > 0) {
          let ratio = 1
          if (distance < p.scrollHeight) {
            ratio = distance / p.scrollHeight
          }

          const wordsCount = p.textContent.trim().split(' ').length
          const duration = wordsCount / (this.wordsPerMinute / 60) * 1000 * ratio

          $body.animate({
            scrollTop: bottom
          }, {
            duration,
            easing: 'linear',
            start: () => {
              this.isScrolling = true
            },
            done: () => {
              this.isScrolling = false
            }
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.teleprompter {
  width: 36%;
  margin: 0 auto;
}

.teleprompter-header {
  height: 65px;
  background-color: white;

  @media (max-width: 991.98px) {
    height: 55px;
  }
}

.teleprompter-body,
.teleprompter-toolbar {
  background-color: rgba(255, 255, 255, .8);
}

.teleprompter-toolbar {
  overflow-x: auto;
  @media (min-width: 992px) {
    overflow-x: initial;
  }
}

.teleprompter-body {
  overflow: auto;
  max-height: 140px;
  text-align: center;
  position: relative;

  p {
    min-height: 24px;
    margin-bottom: 0;
  }
}

.checkbox {
  font-size: 0.8rem;
}

.slider {
  width: 140px;
}

</style>
